import React, { useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useSelector } from 'react-redux'

import { apiService } from '@/api'
import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { CHANNEL_TYPE } from '@/utils/constants'
import { ExternalCallIdsLink } from '@/views/QACopilot/components/ExternalCallIdsLink'
import { CHART } from '@/components/charts/chartPalette'
import { buildDashboardParams, useDashboardOrgId } from '../helpers/dashboardParams'
import { callPercentage, isCallScored, tierForCall } from '../helpers/format'
import { ScoredCallsBrowser } from './ScoredCallsBrowser'
import { ChartCard } from '../components/ChartCard'
import {
  EntityDrawerShell,
  EntityDrawerStats,
  buildDrawerScopeFilters,
  buildScoreTrendOption,
  mapFailingCriteriaRows,
  useDrawerFetches,
} from './entityDrawer.helpers'

export const AgentDetailDrawer = ({ agent, onClose, onEnterViewAs }) => {
  const filters = useSelector((state) => state.qaCopilot.filters)
  // Absent until filters apply; without the default the criterion links
  // below lose their channel scope on first render (as OverviewCardsRow
  // defaults it too).
  const appliedCallType =
    useSelector((state) => state.qaCopilot.data.appliedCallType) || CHANNEL_TYPE.CALL
  const orgId = useDashboardOrgId()

  const params =
    agent && orgId
      ? buildDashboardParams(filters, orgId, { user_id: agent.user_id }).toString()
      : null
  // The stat tiles derive from the SAME scored-calls request the
  // browser section fires (identical URL, so the in-flight coalescer
  // makes it one network call). One source, one denominator -- tiles
  // computed from mixed sources (click payload, by_date series,
  // distinct-call counts) disagreed with each other and with the
  // visible rows ("3 calls, 50% pass, 1 failed").
  const browserScope = agent
    ? buildDrawerScopeFilters(filters, { agents: [{ value: agent.user_id }] })
    : null
  const browserScopeKey = JSON.stringify(browserScope)
  const callsUrl = useMemo(() => {
    if (!browserScope || !orgId) {
      return null
    }
    const callsParams = buildDashboardParams(browserScope, orgId)
    callsParams.set('limit', '500')
    return `${apiService.scorecard}/scoring/scores/clickhouse/calls?${callsParams.toString()}`
  }, [browserScopeKey, orgId])

  const {
    data: [byDay, byCriteria, scoredCalls],
    loading,
    error,
  } = useDrawerFetches(
    params &&
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/multiple/by_date?${params}`,
    params &&
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${params}`,
    callsUrl
  )

  const trendRows = useMemo(() => byDay?.aggregates || [], [byDay])

  const scoreOverTimeOption = useMemo(() => buildScoreTrendOption(trendRows), [trendRows])

  const topFailedRows = useMemo(() => mapFailingCriteriaRows(byCriteria, 8), [byCriteria])

  const topFailedOption = useMemo(() => {
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 130, right: 24, top: 16, bottom: 16 },
      xAxis: { type: 'value', name: '% Failing', max: 100 },
      yAxis: {
        type: 'category',
        data: topFailedRows.map((row) => row.name),
        inverse: true,
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          data: topFailedRows.map((row) => row.value),
          itemStyle: { color: CHART.CRITICAL },
          barWidth: '60%',
        },
      ],
    }
  }, [topFailedRows])

  const stats = useMemo(() => {
    // Exclude partially-scored/unscored calls from the stat tiles: they
    // have no final score (shown as N/A elsewhere), so they must not sit
    // in the calls/avgScore/passRate denominators.
    const scored = (scoredCalls?.items || []).filter(
      (call) => isCallScored(call) && !isNil(callPercentage(call))
    )
    if (isEmpty(scored)) {
      return { calls: null, avgScore: null, passRate: null, failed: null }
    }
    const failed = scored.filter((call) => tierForCall(call) === 'below').length
    const totalPct = scored.reduce((total, call) => total + callPercentage(call), 0)
    return {
      calls: scored.length,
      avgScore: totalPct / scored.length,
      passRate: (100 * (scored.length - failed)) / scored.length,
      failed,
    }
  }, [scoredCalls])

  if (!agent) {
    return null
  }

  return (
    <EntityDrawerShell
      title={agent.full_name || agent.name || `Agent ${agent.user_id}`}
      typeLabel="Agent"
      subtitle={
        (agent.email || agent.username) && (
          <span>
            {' · '}
            {agent.email || agent.username}
          </span>
        )
      }
      onClose={onClose}
    >
      {onEnterViewAs && (
        <Button brand className="viewas-cta" onClick={() => onEnterViewAs(agent)}>
          View dashboard as {agent.full_name?.split(' ')[0] || 'this agent'} →
        </Button>
      )}
      <EntityDrawerStats
        stats={[
          { label: 'Calls', value: isNil(stats.calls) ? '—' : stats.calls.toLocaleString() },
          {
            label: 'Avg Score',
            value: isNil(stats.avgScore) ? '—' : `${stats.avgScore.toFixed(0)}%`,
          },
          {
            label: 'Pass Rate',
            value: isNil(stats.passRate) ? '—' : `${stats.passRate.toFixed(0)}%`,
          },
          { label: 'Failed', value: isNil(stats.failed) ? '—' : stats.failed.toLocaleString() },
        ]}
      />

      <ChartCard
        title="Score Over Time"
        option={scoreOverTimeOption}
        loading={loading}
        error={error}
        isEmpty={isEmpty(trendRows)}
        errorMessage="Couldn’t load this agent’s score history."
        emptyMessage={
          <>
            No scored calls for this agent in the current window.
            {Array.isArray(filters?.scorecards) && !isEmpty(filters.scorecards) && (
              <>
                {' '}
                Try clearing the scorecard filter. The leaderboard may include this agent based on
                other scorecards.
              </>
            )}
          </>
        }
      />

      <ChartCard
        title="Top Failed Criteria"
        option={topFailedOption}
        height={280}
        // The bars answer "how often"; these links answer "which calls".
        // Real anchors rather than bar clicks -- ECharts bars aren't
        // keyboard reachable, and this set is the coach's next stop.
        footer={
          <div className="chart-card-links">
            <span className="chart-card-links-label">Open failing calls:</span>
            {topFailedRows.map((row) => (
              <ExternalCallIdsLink
                key={row.name}
                callIds={row.row.call_ids}
                label={`${row.name} (${row.row.call_count})`}
                channelType={appliedCallType}
                className="chart-card-link"
              />
            ))}
          </div>
        }
        loading={loading}
        error={error}
        isEmpty={isEmpty(topFailedRows)}
        errorMessage="Couldn’t load this agent’s failed criteria."
        emptyMessage="No failing criteria for this agent in the current window."
      />

      <Card title="Scored calls">
        <ScoredCallsBrowser
          // Pin to THIS agent; scorecards stay as-is from the filter bar --
          // if none picked, the browser shows the pick-a-scorecard empty state.
          scopeFilters={buildDrawerScopeFilters(filters, { agents: [{ value: agent.user_id }] })}
          orgId={orgId}
        />
      </Card>
    </EntityDrawerShell>
  )
}
